import dayjs from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import { Period } from "./Period";
import { PeriodResolution, PeriodWindow } from "./Metrics.types";
import { ColDef } from "ag-grid-enterprise";

dayjs.extend(quarterOfYear);

export class PeriodRange {
    readonly from: Period;
    readonly to: Period;

    constructor(from: Period, to: Period) {
        this.from = from;
        this.to = to;
    }

    static fromArray(periods: Period[]): PeriodRange | null {
        if (!periods || periods.length === 0) {
            return null;
        }
        const sorted = [...periods].sort((a, b) =>
            a.isBefore(b) ? -1 : a.isAfter(b) ? 1 : 0,
        );
        return new PeriodRange(sorted[0], sorted[sorted.length - 1]);
    }

    static fromDate(
        asOfDate: dayjs.Dayjs,
        window: PeriodWindow,
    ): PeriodRange | null {
        if (window.windowSize < 1 || !asOfDate.isValid()) return null;

        let from: dayjs.Dayjs;
        let to: dayjs.Dayjs;

        if (window.resolution === PeriodResolution.MONTHLY) {
            to = asOfDate.startOf("month");
            from = to.subtract(window.windowSize - 1, "month");
        } else if (window.resolution === PeriodResolution.QUARTERLY) {
            to = asOfDate.startOf("quarter");
            const isCompleteQuarter = asOfDate.isSame(
                asOfDate.endOf("quarter"),
                "day",
            );
            if (!isCompleteQuarter) {
                to = to.subtract(1, "quarter");
            }
            from = to.subtract(window.windowSize - 1, "quarter");
        } else {
            to = asOfDate.startOf("year");
            const isDecember = asOfDate.month() === 11;
            if (!isDecember) {
                to = to.subtract(1, "year");
            }
            from = to.subtract(window.windowSize - 1, "year");
        }

        return new PeriodRange(new Period(from), new Period(to));
    }

    toString(resolution: PeriodResolution = PeriodResolution.MONTHLY): string {
        const from = this.from.getShortName(resolution);
        const to = this.to.getShortName(resolution);
        return this.from.isSameAs(this.to) ? from : `${from} - ${to}`;
    }

    toStringWithFullNames(
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): string {
        const from = this.from.getFullName(resolution);
        const to = this.to.getFullName(resolution);
        return this.from.isSameAs(this.to) ? from : `${from} - ${to}`;
    }

    includes(period: string | Period): boolean {
        return !this.from.isAfter(period) && !this.to.isBefore(period);
    }

    getAllPeriods(
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): Period[] {
        const start = this.from.base;
        const end = this.to.base;
        if (!start.isValid() || !end.isValid()) return [];

        if (resolution === PeriodResolution.MONTHLY) {
            const count = end.diff(start, "month") + 1;
            return Array.from(
                { length: count },
                (_, i) => new Period(start.add(i, "month")),
            );
        }

        if (resolution === PeriodResolution.QUARTERLY) {
            const startTotal = start.year() * 4 + start.quarter() - 1;
            const endTotal = end.year() * 4 + end.quarter() - 1;
            const count = endTotal - startTotal + 1;
            return Array.from({ length: count }, (_, i) => {
                const total = startTotal + i;
                const year = Math.floor(total / 4);
                const quarter = (total % 4) + 1;
                return new Period(dayjs().year(year).quarter(quarter));
            });
        }

        if (resolution === PeriodResolution.YEARLY) {
            const count = end.year() - start.year() + 1;
            return Array.from(
                { length: count },
                (_, i) => new Period(dayjs().year(start.year() + i)),
            );
        }

        return [];
    }

    getYTDPeriods(
        asOf: Period | dayjs.Dayjs,
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): Period[] {
        const date = asOf instanceof Period ? asOf.base : asOf;
        if (!date.isValid()) return [];

        return this.getAllPeriods(resolution).filter((p) => {
            if (p.base.year() !== date.year()) return false;
            return p.base.month() <= date.month();
        });
    }

    getLastNPeriods(
        n: number,
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): Period[] {
        return this.getAllPeriods(resolution).slice(-n);
    }

    getPeriodsAsOf(
        asOf: Period | dayjs.Dayjs,
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
    ): Period[] {
        const date = asOf instanceof Period ? asOf.base : asOf;
        if (!date.isValid()) return [];

        return this.getAllPeriods(resolution).filter(
            (p) => p.base.isBefore(date) || p.base.isSame(date),
        );
    }

    getNumberOfPeriods(resolution: PeriodResolution): number {
        if (!this.from.base.isValid() || !this.to.base.isValid()) return 0;

        if (resolution === PeriodResolution.MONTHLY) {
            return this.to.base.diff(this.from.base, "month") + 1;
        }
        if (resolution === PeriodResolution.QUARTERLY) {
            return this.to.base.diff(this.from.base, "quarter") + 1;
        }
        return this.to.base.diff(this.from.base, "year") + 1;
    }

    toColumns(
        resolution: PeriodResolution = PeriodResolution.MONTHLY,
        additionalFields: Partial<ColDef> = {},
    ) {
        return this.getAllPeriods(resolution).map((period) => ({
            field: period.getShortName(resolution),
            headerName: period.getShortName(resolution),
            ...additionalFields,
        }));
    }
}
